import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Database, RotateCcw, Search } from 'lucide-react'
import { dataCubeApi } from '@/api/data-cube'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { DataCubeListParams } from '@/types/data-cube'

const metricColumns = [
	{ key: 'friend_count', label: '好友' },
	{ key: 'crony_count', label: '加上的好友' },
	{ key: 'breaking_count', label: '破冰' },
	{ key: 'daily_count', label: '一天生活' },
	{ key: 'location_count', label: '定位' },
	{ key: 'gift_count', label: '礼物' },
	{ key: 'medical_count', label: '义诊' },
	{ key: 'photo_count', label: '素颜照' },
	{ key: 'newly_count', label: '新开' },
] as const

export function UserSummaryPage() {
	const [filters, setFilters] = useState<DataCubeListParams>({ scope: 'my' })
	const [searchFilters, setSearchFilters] = useState<DataCubeListParams>({ scope: 'my' })
	const { data: batches = [] } = useQuery({ queryKey: ['data-cube', 'batches'], queryFn: dataCubeApi.batches })
	const { data = [], isLoading, isFetching } = useQuery({
		queryKey: ['data-cube', 'user-summary', searchFilters],
		queryFn: () => dataCubeApi.userSummary(searchFilters),
	})

	const handleSearch = () => setSearchFilters(cleanParams(filters))
	const handleReset = () => {
		setFilters({ scope: 'my' })
		setSearchFilters({ scope: 'my' })
	}

	return (
		<div className="space-y-5">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">个人数据统计</h1>
				<p className="mt-1 text-sm text-muted-foreground">按员工聚合数据魔方指标</p>
			</div>

			<Card className="rounded-lg">
				<CardContent className="pt-6">
					<div className="flex flex-wrap items-center gap-3">
						<Select value={filters.batch_id ? String(filters.batch_id) : 'all'} onValueChange={(value) => setFilters({ ...filters, batch_id: value === 'all' ? undefined : Number(value) })}>
							<SelectTrigger className="w-[190px]">
								<SelectValue placeholder="全部批次" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">全部批次</SelectItem>
								{batches.map((batch) => (
									<SelectItem key={batch.id} value={String(batch.id)}>{batch.batch_name}</SelectItem>
								))}
							</SelectContent>
						</Select>
					<DepartmentScopeFilter value={{ scope: filters.scope || 'my', department_id: filters.department_id }} onChange={(scopeValue) => setFilters({ ...filters, department_id: undefined, ...scopeValue, /* 本页未启用「无归属」 */ scope: scopeValue.scope === 'unassigned' ? undefined : scopeValue.scope })} />
						<DateRangePicker value={{ start_date: filters.start_date, end_date: filters.end_date }} onChange={(value) => setFilters({ ...filters, ...value })} />
						<Button type="button" onClick={handleSearch} disabled={isFetching}>
							<Search className="mr-1.5 h-4 w-4" />
							查询
						</Button>
						<Button type="button" variant="outline" onClick={handleReset}>
							<RotateCcw className="mr-1.5 h-4 w-4" />
							重置
						</Button>
					</div>
				</CardContent>
			</Card>

			<Card className="overflow-hidden rounded-lg">
				<CardContent className="p-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[980px]">
							<TableHeader>
								<TableRow>
									<TableHead className="w-40">员工</TableHead>
									<TableHead className="w-20">记录数</TableHead>
									{metricColumns.map((column) => <TableHead key={column.key}>{column.label}</TableHead>)}
								</TableRow>
							</TableHeader>
							<TableBody>
								{isLoading ? (
									<TableRow>
										<TableCell colSpan={11} className="h-28 text-center">加载中...</TableCell>
									</TableRow>
								) : data.length === 0 ? (
									<TableRow>
										<TableCell colSpan={11} className="h-32 text-center text-muted-foreground">
											<Database className="mx-auto mb-2 h-8 w-8" />
											暂无个人统计
										</TableCell>
									</TableRow>
								) : data.map((row) => (
									<TableRow key={row.user_id || 'summary'} className={row.user_id === 0 ? 'bg-muted/40 font-medium' : undefined}>
										<TableCell>{row.user_id === 0 ? '汇总' : row.user?.nickname || row.user?.username || row.user_id}</TableCell>
										<TableCell>{row.total}</TableCell>
										{metricColumns.map((column) => <TableCell key={column.key}>{row[column.key] ?? 0}</TableCell>)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}

function cleanParams(params: DataCubeListParams): DataCubeListParams {
	return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '')) as DataCubeListParams
}
